
import { of as observableOf, Observable } from 'rxjs';
import { Injectable } from '@angular/core';

import { HisBackendService } from 'hisng';

import { ResearchContract } from 'app/fs/res/common/model/finance/research-contract-classes';
import { BudgetSource } from 'app/fs/res/finance/model/budget-source/budget-source';

@Injectable()
export class BudgetSourceService {

    constructor( private backendService: HisBackendService ) { }

    /**
     * getBudgetSourceById service call to load budgetSource with Id.
     *
     * @param id Id of budgetSource to load.
     */
    getBudgetSourceById( id: number ): Observable<BudgetSource> {
        return this.backendService.getData( `api/v1/fs/res/budgetSource/${id}` );
    }

    // generate a new budgetSource
    getNewBudgetSource(): Observable<BudgetSource> {
        return observableOf( new BudgetSource() );
    }

    /* save new budgetSource data as funding program
     * @param budgetSource BudgetSource to save.
     */
    saveBudgetSourceAsFundingProgram( budgetSource: BudgetSource ): Observable<BudgetSource> {
        return this.backendService.postData( 'api/v1/fs/res/fundingProgram', budgetSource );
    }

    /**
     * save new or existing budgetSource data.
     *
     * @param budgetSource BudgetSource to save.
     */
    saveBudgetSource( budgetSource: BudgetSource ): Observable<BudgetSource> {
        if ( budgetSource.id == null ) {
            return this.backendService.postData( 'api/v1/fs/res/budgetSource', budgetSource );
        } else {
            return this.backendService.putData( `api/v1/fs/res/budgetSource/${budgetSource.id}`, budgetSource );
        }
    }

    /**
     * delete service call to delete single budgetSource (without childred) from database.
     * returns Observable containing the result of operation.
     *
     * @param budgetSourceId the Id of budgetSource to delete.
     * @return number of deleted items
     */
    deleteBudgetSource( budgetSourceId: number ): Observable<number> {
        return this.backendService.delete( `api/v1/fs/res/budgetSource/${budgetSourceId}` );
    }

    /**
     * check if budgetSource can be deleted and has not further references to other objects.
     *
     * @param budgetSourceId Id of budgetSource
     */
    canDeleteBudgetSource( budgetSourceId: number ): Observable<string> {
        return this.backendService.getData( `api/v1/fs/res/canDeleteBudgetSource/${budgetSourceId}` );
    }

    /**
     * get budget sources from research contract objects
     *
     * @param researchContracts containing information about budget sources
     */
    getTypesOfObject( researchContracts: ResearchContract[], searchText: string ): string[] {
        let budgetSourceTypes: string[] = [];
        for ( let f of researchContracts ) {
            if ( f.budgetSource ) {
                if ( f.budgetSource.defaulttext.toLowerCase().includes( searchText.toLowerCase() ) ) {
                    budgetSourceTypes.push( f.budgetSource.defaulttext );
                }
            }
        }
        return budgetSourceTypes;
    }

    /* get all budget sources type of funding programs */
    getFundingProgramTypes( searchText: string, filterType?: boolean ): Observable<BudgetSource[]> {
        if ( filterType ) {
            return this.backendService.getData( `api/v1/fs/res/fundingPrograms?q=${searchText}&f=A` );
        } else {
            return this.backendService.getData( `api/v1/fs/res/fundingPrograms?q=${searchText}` );
        }
    }

    /* get all budget sources type of rights of use */
    getRightOfUseTypes( searchText: string ): Observable<BudgetSource[]> {
        return this.backendService.getData( `api/v1/fs/res/rightsOfUse?q=${searchText}` );
    }

    /* get all budget sources */
    getValues( searchText: string ): Observable<BudgetSource[]> {
        return this.backendService.getData( `api/v1/fs/res/budgetSource?q=${searchText}` );
    }

    logline( logtext: string ) {
        console.error( logtext );
        let data = {
            fileName: 'none',
            message: logtext,
            stack: '------',
        };

        return this.backendService.postData( 'api/v1/cs/sys/log/error', data );
    }
}
